import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiCheck, FiZap, FiEye, FiDroplet, FiShoppingBag, FiSmartphone, FiHome, FiBook, FiMonitor } from "react-icons/fi";
import { FaTshirt, FaHamburger, FaPuzzlePiece } from "react-icons/fa";
import { GiLipstick, GiDiamondRing, GiRunningShoe, GiHandBag } from "react-icons/gi";
import styles from "./TemplateCard.module.css";
import PresetPreviewModal from "./PresetPreviewModal";

// Template completion status.
//   "complete" — every storefront page is production-ready
//   "partial"  — core pages work, long-tail pages still use default fallback
//   "coming"   — not implemented yet; Activate button disabled
const TEMPLATE_STATUS = {
  default: "complete",
  cosmetics: "partial",
};

// Industry-appropriate emoji shown in the preview placeholder when the
// template has no thumbnail image set in the DB and no bundled preview.
const TEMPLATE_EMOJI = {
  default: FiShoppingBag,
  cosmetics: GiLipstick,
  fashion: FaTshirt,
  gadget: FiSmartphone,
  food: FaHamburger,
  home: FiHome,
  books: FiBook,
  toys: FaPuzzlePiece,
  jewelry: GiDiamondRing,
  shoes: GiRunningShoe,
  bags: GiHandBag,
  electronics: FiMonitor,
};

function TemplateCard({ template, isActive, isActivating, onActivate }) {
  const [showPresets, setShowPresets] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const [previewingPreset, setPreviewingPreset] = useState(null);
  const navigate = useNavigate();

  const status = TEMPLATE_STATUS[template.name] || "coming";
  const isComingSoon = status === "coming";
  const isPartial = status === "partial";
  const isComplete = status === "complete";
  const Emoji = TEMPLATE_EMOJI[template.name] || FiShoppingBag;
  const firstPreset = template.colors?.[0]?.values || {};
  const gradientStart = firstPreset.HeaderColor || "#5c6ac4";
  const gradientEnd = firstPreset.BuyNowColor || firstPreset.AccentColor || "#8b2252";
  // Prefer DB thumbnail, then bundled per-template preview, then gradient+emoji fallback.
  const previewSrc = template.thumbnail || `/images/templates/${template.name}.jpg`;
  const useImage = !imgFailed && previewSrc;

  return (
    <div className={`${styles.card} ${isActive ? styles.active : ""} ${isComingSoon ? styles.comingSoonCard : ""}`}>
      <div className={styles.imageWrap}>
        {useImage ? (
          <img
            src={previewSrc}
            alt={template.label}
            className={styles.image}
            loading="lazy"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div
            className={styles.placeholder}
            style={{ background: `linear-gradient(135deg, ${gradientStart}, ${gradientEnd})` }}
            aria-label={`${template.label} preview`}
          >
            <span className={styles.placeholderEmoji} aria-hidden="true"><Emoji /></span>
            <span className={styles.placeholderLabel}>{template.label}</span>
          </div>
        )}
        {isActive && <span className={styles.activeBadge}>Active</span>}
        <span className={styles.industryBadge}>{template.industry}</span>
        {isComplete && <span className={styles.statusComplete}><FiCheck /> Complete</span>}
        {isPartial && <span className={styles.statusPartial}><FiZap /> Partial</span>}
        {isComingSoon && <span className={styles.comingSoonBadge}>Coming Soon</span>}
      </div>

      <div className={styles.info}>
        <h3 className={styles.name}>{template.label}</h3>
        <p className={styles.labelBn}>{template.labelBn}</p>
        <p className={styles.desc}>{template.description}</p>

        <div className={styles.presetSwatches}>
          {template.colors?.map((preset) => (
            <button
              key={preset.name}
              type="button"
              className={styles.swatch}
              title={`Click to preview ${preset.name}`}
              onClick={() => !isComingSoon && setPreviewingPreset(preset)}
              disabled={isComingSoon}
            >
              <span
                className={styles.swatchDot}
                style={{ background: preset.values?.HeaderColor || "#333" }}
              />
              <span
                className={styles.swatchDot}
                style={{ background: preset.values?.BuyNowColor || "#008060" }}
              />
              <span
                className={styles.swatchDot}
                style={{ background: preset.values?.AccentColor || "#666" }}
              />
              <span className={styles.swatchLabel}>{preset.name}</span>
            </button>
          ))}
        </div>
      </div>

      <div className={styles.actions}>
        {!isComingSoon && (
          <a
            className={styles.previewBtn}
            href={`/template-preview/${template.name}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FiEye /> Live Preview
          </a>
        )}
        {isActive ? (
          <>
            <button className={styles.activatedBtn} disabled>
              <FiCheck /> Activated
            </button>
            <button
              className={styles.editColorsBtn}
              onClick={() => navigate(`/template/edit/${template.name}`)}
            >
              <FiDroplet /> Edit Colors
            </button>
          </>
        ) : isComingSoon ? (
          <button className={styles.comingSoonBtn} disabled title="This template is not ready yet">
            Coming Soon
          </button>
        ) : (
          <>
            {!showPresets ? (
              <button
                className={styles.activateBtn}
                onClick={() => {
                  if (template.colors?.length > 1) {
                    setShowPresets(true);
                  } else {
                    onActivate(template.colors?.[0]?.name);
                  }
                }}
                disabled={isActivating}
              >
                {isActivating ? "Activating..." : "Activate"}
              </button>
            ) : (
              <div className={styles.presetPicker}>
                <p className={styles.presetTitle}>Choose a color preset:</p>
                {template.colors?.map((preset) => (
                  <button
                    key={preset.name}
                    className={styles.presetBtn}
                    onClick={() => {
                      onActivate(preset.name);
                      setShowPresets(false);
                    }}
                    disabled={isActivating}
                    style={{
                      borderLeft: `4px solid ${preset.values?.HeaderColor || "#333"}`,
                    }}
                  >
                    {preset.name}
                    {preset.isDefault && " (default)"}
                  </button>
                ))}
                <button
                  className={styles.cancelBtn}
                  onClick={() => setShowPresets(false)}
                >
                  Cancel
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {previewingPreset && (
        <PresetPreviewModal
          templateName={template.name}
          presetName={previewingPreset.name}
          presetLabel={`${template.label} · ${previewingPreset.name}`}
          isActivating={isActivating}
          onClose={() => setPreviewingPreset(null)}
          onActivate={() => {
            onActivate(previewingPreset.name);
            setPreviewingPreset(null);
          }}
        />
      )}
    </div>
  );
}

export default TemplateCard;
